import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

const colors = [
  "#4285f4", // Blue
  "#ea4335", // Red
  "#34a853", // Green
  "#fbbc04", // Yellow
  "#9334e6", // Purple
  "#e91e63", // Pink
  "#00bcd4", // Cyan
  "#ff5722", // Orange
];

const emojis = ["📋", "📥", "💼", "👤", "🏠", "🛒", "📚", "❤️", "⭐", "🎯", "🎨", "🎵"];

export default function ListModal({ open, onOpenChange, list, onSave }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(colors[0]);
  const [emoji, setEmoji] = useState("📋");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (list) {
      setName(list.name || "");
      setColor(list.color || colors[0]);
    } else {
      setName("");
      setColor(colors[0]);
      setEmoji("📋");
    }
  }, [list, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    setLoading(true);
    try {
      const payload = { name: name.trim(), color, icon: "list" };
      const url = list ? `${API}/lists/${list.list_id}` : `${API}/lists`;
      const method = list ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const savedList = await response.json();
        onSave(savedList);
        onOpenChange(false);
        toast.success(list ? "List updated" : "List created");
      } else {
        throw new Error("Failed to save list");
      }
    } catch (error) {
      toast.error("Failed to save list");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm bg-[#2d3136] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{list ? "Edit List" : "New List"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Preview */}
          <div className="flex justify-center py-4">
            <div className="text-5xl">{emoji}</div>
          </div>

          {/* Name */}
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="List name"
            className="text-center text-lg bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="list-name-input"
            autoFocus
          />

          {/* Emojis */}
          <div className="space-y-2">
            <span className="text-sm font-medium text-gray-400">Icon</span>
            <div className="flex flex-wrap justify-center gap-2">
              {emojis.map((e) => (
                <button
                  key={e}
                  type="button"
                  className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all",
                    emoji === e ? "bg-white/20 scale-110" : "bg-white/5 hover:bg-white/10"
                  )}
                  onClick={() => setEmoji(e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="space-y-2">
            <span className="text-sm font-medium text-gray-400">Color</span>
            <div className="flex justify-center gap-3">
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={cn(
                    "w-8 h-8 rounded-full transition-all",
                    color === c ? "ring-2 ring-offset-2 ring-offset-[#2d3136] scale-110" : "hover:scale-105"
                  )}
                  style={{ backgroundColor: c, ringColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
              data-testid="save-list-btn"
            >
              {loading ? "Saving..." : list ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
